import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Building2, TrendingUp, ArrowRight, Clock, Sun, CheckCircle2, Rocket, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader, EmptyState } from '@/components/common/Feedback';
import { QuickCaptureDialog } from '@/components/capture/QuickCaptureDialog';
import { OnboardingWizard } from '@/components/projects/OnboardingWizard';
import { OpportunityDrawer } from '@/components/opportunities/OpportunityDrawer';
import { StrengthBadge, ClassificationBadge, AmountOrStatus } from '@/components/opportunities/OppBadges';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { noticeCountdown, NOTICE_TONE } from '@/lib/format';

const greeting = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const TONES = {
  slate: { wrap: 'bg-slate-100 ring-slate-200', icon: 'text-slate-600' },
  brand: { wrap: 'bg-brand-50 ring-brand-100', icon: 'text-brand-600' },
  money: { wrap: 'bg-money-50 ring-money-100', icon: 'text-money-600' },
};

const Stat = ({ icon: Icon, label, value, tone = 'slate', testid }) => {
  const t = TONES[tone] || TONES.slate;
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`rounded-xl p-2.5 ring-1 ${t.wrap}`}>
        <Icon className={`h-5 w-5 ${t.icon}`} />
      </div>
      <div>
        <div className="text-xl font-semibold text-ink-900 font-mono">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </Card>
  );
};

export default function TodaysWork() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [wizard, setWizard] = useState(false);
  // Live clock in the user's local timezone — keeps the date/greeting fresh
  // without a page refresh (updates every 30s so it rolls over accurately).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/opportunities').then((r) => setOpps(r.data)),
      api.get('/projects').then((r) => setProjects(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const attention = useMemo(
    () => opps.filter((o) => ['new', 'reviewed'].includes(o.opportunity_status))
      .sort((a, b) => (b.confidence || 0) - (a.confidence || 0)),
    [opps]
  );
  const pricedCount = useMemo(
    () => opps.filter((o) => ['new', 'reviewed', 'notice_sent', 'submitted'].includes(o.opportunity_status) && o.pricing_status === 'fully_priced').length,
    [opps]
  );
  // Opportunities still needing a notice, soonest deadline first.
  const deadlines = useMemo(
    () => opps
      .filter((o) => ['new', 'reviewed'].includes(o.opportunity_status) && o.notice_days_left !== null && o.notice_days_left !== undefined)
      .sort((a, b) => a.notice_days_left - b.notice_days_left)
      .slice(0, 6),
    [opps]
  );

  if (loading) return <Loader label="Loading your day..." />;

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-2xl bg-ink-900 text-white px-6 py-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-transparent pointer-events-none" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-slate-300 text-xs font-medium">
              <Sun className="h-4 w-4 text-money" />
              {now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <h2 className="text-2xl font-semibold mt-1 font-display" data-testid="today-greeting">{greeting(now)}, {firstName}</h2>
            <p className="text-sm text-slate-300 mt-1">
              {projects.length === 0
                ? 'Set up your first project and Recoup will start finding opportunities for you.'
                : attention.length > 0
                  ? `You have ${attention.length} opportunit${attention.length === 1 ? 'y' : 'ies'} to review.`
                  : 'You are all caught up. Capture a new communication to get started.'}
            </p>
          </div>
          {projects.length === 0 ? (
            <Button size="lg" className="bg-money hover:bg-money-700 shadow-lifted" onClick={() => setWizard(true)} data-testid="today-get-started">
              <Rocket className="h-4 w-4 mr-2" />Set up first project
            </Button>
          ) : (
            <QuickCaptureDialog onDone={load} trigger={
              <Button size="lg" className="bg-money hover:bg-money-700 shadow-lifted" data-testid="today-add-anything">
                <TrendingUp className="h-4 w-4 mr-2" />Add Anything
              </Button>
            } />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat icon={Target} label="Open opportunities" value={attention.length + opps.filter((o) => ['notice_sent', 'submitted'].includes(o.opportunity_status)).length} tone="brand" testid="stat-open-opps" />
        <Stat icon={TrendingUp} label="Fully priced" value={pricedCount} tone="money" testid="stat-open-value" />
        <Stat icon={Building2} label="Active projects" value={projects.length} tone="slate" testid="stat-projects" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Needs your attention */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink-900">Needs your attention</h3>
            <button onClick={() => navigate('/opportunities')} className="text-xs text-brand hover:underline flex items-center gap-1" data-testid="view-all-opps">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <Card className="p-0 divide-y divide-slate-100">
            {attention.length === 0 ? (
              <div className="py-10">
                <EmptyState icon={CheckCircle2} title="Nothing needs review" description="New opportunities will show up here as you capture communications." />
              </div>
            ) : attention.slice(0, 6).map((o) => (
              <button key={o.id} onClick={() => setSelected(o.id)} data-testid={`attention-${o.id}`}
                className="w-full text-left px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 text-sm truncate">{o.title}</div>
                  <div className="text-xs text-slate-500 truncate">{o.project_name}</div>
                </div>
                <span className="hidden sm:inline-flex"><ClassificationBadge classification={o.classification} /></span>
                <StrengthBadge strength={o.strength} confidence={o.confidence} />
                <div className="w-20 sm:w-24 text-right"><AmountOrStatus opportunity={o} compact testid={`attention-amount-${o.id}`} /></div>
              </button>
            ))}
          </Card>
        </div>

        {/* Upcoming notice deadlines with live countdown */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-ink-900">Upcoming notice deadlines</h3>
          <Card className="p-0">
            {deadlines.length === 0 ? (
              <div className="flex flex-col items-center text-center py-8 px-5">
                <div className="rounded-2xl bg-money-50 ring-1 ring-money-100 p-3 mb-3"><Clock className="h-6 w-6 text-money-600" /></div>
                <div className="text-sm font-semibold text-ink-900">No notices pending</div>
                <p className="text-xs text-slate-500 mt-1">When an opportunity needs a notice, its countdown appears here.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {deadlines.map((o) => {
                  const cd = noticeCountdown(o.notice_days_left);
                  return (
                    <button key={o.id} onClick={() => setSelected(o.id)} data-testid={`deadline-${o.id}`}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{o.title}</div>
                        <div className="text-[11px] text-slate-500 truncate">{o.project_name}</div>
                      </div>
                      {cd && <span className={cn('shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold', NOTICE_TONE[cd.tone])}>
                        {cd.urgent && <AlertTriangle className="h-3 w-3" />}{cd.label}
                      </span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      <OpportunityDrawer opportunityId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
      <OnboardingWizard open={wizard} onOpenChange={setWizard} onComplete={load} />
    </div>
  );
}
